import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FolderOpen, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { ServiceCategory } from "@/gql/graphql";
import { cn } from "@/lib/utils";
import type { ServiceRowData } from "../../types";
import { CategoryRow } from "./category-row";
import { ServiceRow } from "./service-row";

interface ServicesTableProps {
  categories: ServiceCategory[];
  servicesByCategory: Record<string, ServiceRowData[]>;
  salonId: string;
  expandedCategories: Set<string>;
  onToggleCategory: (categoryId: string) => void;
  onEditCategory: (category: ServiceCategory) => void;
  onCreateService: (categoryId?: string) => void;
  onDeleteService: (service: ServiceRowData) => void;
  onArchiveService?: (service: ServiceRowData) => void;
  onRestoreService?: (service: ServiceRowData) => void;
  isDragInProgress?: boolean;
  isUpdating?: boolean;
  isSearching?: boolean;
  isEditMode?: boolean;
  serviceDragInProgress?: boolean;
  isUpdatingServiceSortOrder?: boolean;
}

interface SortableCategorySectionProps {
  category: ServiceCategory;
  services: ServiceRowData[];
  salonId: string;
  isExpanded: boolean;
  onToggleCategory: (categoryId: string) => void;
  onEditCategory: (category: ServiceCategory) => void;
  onCreateService: (categoryId?: string) => void;
  onDeleteService: (service: ServiceRowData) => void;
  onArchiveService?: (service: ServiceRowData) => void;
  onRestoreService?: (service: ServiceRowData) => void;
  isDragInProgress?: boolean;
  isUpdating?: boolean;
  hasActiveServices?: boolean;
  isSearching?: boolean;
  isEditMode?: boolean;
  serviceDragInProgress?: boolean;
  isUpdatingServiceSortOrder?: boolean;
}

function SortableCategorySection({
  category,
  services,
  salonId,
  isExpanded,
  onToggleCategory,
  onEditCategory,
  onCreateService,
  onDeleteService,
  onArchiveService,
  onRestoreService,
  isDragInProgress = false,
  isUpdating = false,
  hasActiveServices = false,
  isSearching = false,
  isEditMode = false,
  serviceDragInProgress = false,
  isUpdatingServiceSortOrder = false,
}: SortableCategorySectionProps) {
  const { t } = useTranslation("services");
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: category.id,
    disabled: isDragInProgress || isUpdating || isSearching,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <TableBody
      ref={setNodeRef}
      style={style}
      className={cn(isDragging && "opacity-50")}
      {...attributes}
    >
      {/* Category Row */}
      <CategoryRow
        category={category}
        serviceCount={services.length}
        salonId={salonId}
        isExpanded={isExpanded}
        onToggleExpanded={() => onToggleCategory(category.id)}
        onEditCategory={onEditCategory}
        onCreateService={onCreateService}
        isDragInProgress={isDragInProgress}
        isUpdating={isUpdating}
        hasActiveServices={hasActiveServices}
        isSearching={isSearching}
        isEditMode={isEditMode}
        isDragOverlay={false}
        dragListeners={listeners}
      />

      {/* Service Rows - only show if expanded */}
      {isExpanded &&
        (services.length === 0 ? (
          <TableRow className="hover:bg-transparent">
            {!isSearching && (
              <TableCell
                className={cn("w-8 p-2", isEditMode ? "sm:table-cell" : "hidden sm:table-cell")}
              />
            )}
            <TableCell colSpan={isSearching ? 5 : 5}>
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-muted/30 flex items-center justify-center">
                  <FolderOpen className="w-6 h-6 text-muted-foreground/60" />
                </div>
                <h4 className="font-medium text-foreground mb-1">{t("table.noServicesYet")}</h4>
                <p className="text-sm text-muted-foreground">{t("table.categoryHasNoServices")}</p>
              </div>
            </TableCell>
          </TableRow>
        ) : (
          <SortableContext items={services.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            {services.map((service) => (
              <ServiceRow
                key={service.id}
                service={service}
                onDelete={onDeleteService}
                onArchive={onArchiveService}
                onRestore={onRestoreService}
                isSearching={isSearching}
                isEditMode={isEditMode}
                isDragInProgress={serviceDragInProgress}
                isUpdating={isUpdatingServiceSortOrder}
              />
            ))}
          </SortableContext>
        ))}
    </TableBody>
  );
}

export function ServicesTable({
  categories,
  servicesByCategory,
  salonId,
  expandedCategories,
  onToggleCategory,
  onEditCategory,
  onCreateService,
  onDeleteService,
  onArchiveService,
  onRestoreService,
  isDragInProgress = false,
  isUpdating = false,
  isSearching = false,
  isEditMode = false,
  serviceDragInProgress = false,
  isUpdatingServiceSortOrder = false,
}: ServicesTableProps) {
  const { t } = useTranslation("services");
  if (categories.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center border rounded-lg">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted/50 flex items-center justify-center">
          <FolderOpen className="w-8 h-8 text-muted-foreground/60" />
        </div>
        <h3 className="font-medium text-foreground mb-2">{t("table.noServiceCategoriesYet")}</h3>
        <p className="text-sm text-muted-foreground mb-4 max-w-sm">
          {t("table.createFirstCategory")}
        </p>
        <Button onClick={() => onCreateService()}>
          <Plus className="w-4 h-4 mr-2" />
          {t("table.createFirstCategoryButton")}
        </Button>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden bg-background">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent h-14">
            {/* Mobile: Show drag in edit mode, Desktop: Show drag when not searching */}
            {!isSearching && (
              <TableHead
                className={cn("w-12 sm:w-8", isEditMode ? "sm:table-cell" : "hidden sm:table-cell")}
              />
            )}

            <TableHead className="text-base font-semibold">
              {t("table.headers.serviceCategory")}
            </TableHead>
            <TableHead className="hidden sm:table-cell w-40 text-center text-base font-semibold">
              {t("table.headers.status")}
            </TableHead>
            <TableHead className="hidden sm:table-cell w-28 text-center text-base font-semibold">
              {t("table.headers.duration")}
            </TableHead>
            <TableHead className="hidden sm:table-cell w-32 text-center text-base font-semibold">
              {t("table.headers.price")}
            </TableHead>

            {/* Actions/Delete Column */}
            <TableHead className="w-12 sm:w-32 text-right text-base font-semibold">
              {t("table.headers.actions")}
            </TableHead>
          </TableRow>
        </TableHeader>

        {/* Each category gets its own TableBody for drag and drop */}
        {categories.map((category) => {
          const categoryServices = servicesByCategory[category.id] || [];
          const isExpanded = expandedCategories.has(category.id);
          const hasActiveServices = categoryServices.filter((s) => s.isActive).length > 0;

          return (
            <SortableCategorySection
              key={category.id}
              category={category}
              services={categoryServices}
              salonId={salonId}
              isExpanded={isExpanded}
              onToggleCategory={onToggleCategory}
              onEditCategory={onEditCategory}
              onCreateService={onCreateService}
              onDeleteService={onDeleteService}
              onArchiveService={onArchiveService}
              onRestoreService={onRestoreService}
              isDragInProgress={isDragInProgress}
              isUpdating={isUpdating}
              hasActiveServices={hasActiveServices}
              isSearching={isSearching}
              isEditMode={isEditMode}
              serviceDragInProgress={serviceDragInProgress}
              isUpdatingServiceSortOrder={isUpdatingServiceSortOrder}
            />
          );
        })}
      </Table>
    </div>
  );
}
